import { ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { DashboardNavbar } from './DashboardNavbar';
import { RailSidebar } from './RailSidebar';
import { BottomNav } from './BottomNav';
import { PageTransition } from '@/components/ui/PageTransition';
import { CanvaImportMinimized } from '@/components/canva/CanvaImportMinimized';
import { PageTitleProvider } from '@/contexts/PageTitleContext';
import { EmailVerificationBanner } from '@/components/EmailVerificationBanner';
import { usePurchaseCelebration } from '@/hooks/usePurchaseCelebration';
import { InstallBar } from '@/components/pwa';
import { GlobalComposerHost } from '@/components/composer-host';
import { ReleaseDrawerHost } from '@/components/release/ReleaseDrawerHost';

/**
 * Dashboard Layout Component
 *
 * Main layout wrapper for authenticated pages
 * Contains:
 * - Fixed top navbar (65.6px / 4.1rem height)
 * - Rail sidebar (desktop ≥1024px, 88px fixed width)
 * - Bottom navigation (mobile <1024px, 64px fixed height)
 * - Content column clears the fixed navbar (pt-navbar-height); <main> adds
 *   its own pt-6 spacing on top, so the optional EmailVerificationBanner can
 *   sit flush under the navbar as a true sibling of <main> (same px-6 gutter)
 * - Light gray background (#F8F9FA)
 *
 * Supports both:
 * - children prop (legacy, for backwards compatibility)
 * - <Outlet /> (for nested routes - RECOMMENDED)
 */

interface DashboardLayoutProps {
  children?: ReactNode;
}

export const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const isShopPage = ['/shop', '/shop/checkout'].includes(useLocation().pathname);
  const { user } = useAuth();
  const { t } = useTranslation('settings');
  // Fire confetti + success toast once when arriving from a completed purchase
  usePurchaseCelebration();

  return (
    <PageTitleProvider>
      <div className="min-h-screen bg-gray-50">
        {/* Rail Sidebar (Desktop) */}
        <RailSidebar />

        {/* Main Content Area */}
        <div className="lg:ml-[88px] flex flex-col min-h-screen">
          {/* Navbar */}
          <DashboardNavbar />

          {/* Content column — clears the fixed navbar here (once), so the banner
              (if rendered) and <main> are true siblings sharing the identical
              `px-6` gutter. No negative-margin breakout on the banner: it's
              naturally full-width and its edges line up with the page content
              below it, at every viewport width. Collapses to nothing when null. */}
          <div className="flex-1 flex flex-col pt-navbar-height">
            <EmailVerificationBanner />
            {user?.needsPlan && !isShopPage && (
              <div className="border-b border-primary-100 bg-primary-50 px-6 py-2.5 text-sm text-gray-800">
                {t('shop_needs_plan_hint')}{' '}
                <Link to="/shop" state={{ entry: 'banner' }} className="rounded border border-transparent font-semibold text-primary-700 underline underline-offset-2 focus-visible:border-primary-700">{t('shop_choose_plan')}</Link>
              </div>
            )}
            <main className={`flex-1 pb-[calc(var(--bottom-nav-h)+10px+env(safe-area-inset-bottom)+var(--floating-banner-h)+var(--floating-install-h))] ${isShopPage ? 'px-0 pt-0 lg:pb-0' : 'px-6 pt-6 lg:pb-[calc(1.5rem+var(--floating-banner-h))]'}`}>
              <PageTransition>
                {children || <Outlet />}
              </PageTransition>
            </main>
          </div>
        </div>

        {/* Bottom Navigation (Mobile) */}
        <BottomNav />

        {/* PWA Install Bar (Mobile/Tablet) - sits above BottomNav */}
        <InstallBar />

        {/* Background import progress indicators */}
        <CanvaImportMinimized />

        {/* Global composers (post + variation), opened over the current page via openComposer() / the stores */}
        <GlobalComposerHost />

        {/* The ONE Release drawer, opened from anywhere via releaseDrawer.store */}
        <ReleaseDrawerHost />
      </div>
    </PageTitleProvider>
  );
};
